import {handleActions} from 'redux-actions';
import {Record, Set, List, Map} from 'immutable';

import * as BillPayerHelpers from 'web-app/react/entities/bill-payers/helpers';
import * as BillPayerModalActions from 'web-app/react/modals-container/bill-payer-modal/actions';
import {parseDecimalToFloat} from 'web-app/util/parsers';
import BillPayerShare from 'web-app/react/entities/bill-payers/common/bill-payer-share';

import * as Actions from './actions';

const BillPayersState = new Record({
    show: false,
    showExample: false,
    splitExampleTotal: '',
    childId: '',
    institutionId: '',
    saving: false,
    removingId: null,
    validationErrors: List(),
    editingShares: Map(),

    editSplitConfiguration: null,

    hasEditedIds: Set(),
});

const convertToPercentageShare = share => {
    const newPercentageShare = Math.max(0, Math.min(share, 100));
    return newPercentageShare / 100;
};

export default handleActions(
    {
        [Actions.reset.type]: () => new BillPayersState(),
        [Actions.show.type]: (state, {payload}) =>
            new BillPayersState({
                show: true,
                childId: payload.childId,
                institutionId: payload.institutionId,
                editSplitConfiguration: payload.splitConfiguration,
            }),
        [Actions.showExample.type]: (state, {payload}) =>
            state.merge({
                showExample: payload.show,
                splitExampleTotal: '',
            }),
        [Actions.updateExampleTotal.type]: (state, {payload}) =>
            state.merge({
                splitExampleTotal: payload.newTotal,
            }),
        [BillPayerModalActions.addBillPayerToChildSuccess.type]: (state, {payload}) => {
            if (state.childId !== payload.childId) {
                return state;
            }

            const {validationErrors, splitConfiguration: editSplitConfiguration} =
                BillPayerHelpers.updateAndValidateConfiguration(
                    payload.suggestedSplitConfiguration,
                    state.hasEditedIds,
                );

            return state.merge({
                validationErrors,
                editSplitConfiguration,
            });
        },
        [Actions.moveDown.type]: (state, {payload}) => {
            const {billPayerId, actualEditSplitConfiguration} = payload;

            const currentConfigurationIndex = actualEditSplitConfiguration.shares.findIndex(
                bps => bps.billPayerId === billPayerId,
            );

            if (currentConfigurationIndex < 0) {
                return state;
            }

            const currentConfiguration = actualEditSplitConfiguration.shares.get(currentConfigurationIndex);
            const newShares = actualEditSplitConfiguration.shares
                .set(currentConfigurationIndex, actualEditSplitConfiguration.shares.get(currentConfigurationIndex + 1))
                .set(currentConfigurationIndex + 1, currentConfiguration);

            const unevenSplitConfiguration = actualEditSplitConfiguration.merge({
                shares: newShares,
            });

            // Update lock depending on swapped shares
            const tempEditedIds = BillPayerHelpers.getLockedIds(
                state.hasEditedIds,
                billPayerId,
                unevenSplitConfiguration,
            );
            const hasEditedIds = BillPayerHelpers.getLockedIds(
                tempEditedIds,
                newShares.get(currentConfigurationIndex).billPayerId,
                unevenSplitConfiguration,
            );

            const {validationErrors, splitConfiguration: editSplitConfiguration} =
                BillPayerHelpers.updateAndValidateConfiguration(unevenSplitConfiguration, hasEditedIds);

            return state.merge({
                validationErrors,
                hasEditedIds,
                editSplitConfiguration,
            });
        },
        [Actions.shareChanged.type]: (state, {payload}) => {
            const {billPayerId, newShare, actualEditSplitConfiguration} = payload;

            const currentConfiguration = actualEditSplitConfiguration.shares.find(
                bps => bps.billPayerId === billPayerId,
            );

            if (!currentConfiguration) {
                return state;
            }

            const parsedShare = parseDecimalToFloat(newShare) || 0;
            const share =
                currentConfiguration.type === BillPayerShare.CONSTANTS.TYPES.PERCENTAGE
                    ? convertToPercentageShare(parsedShare)
                    : parsedShare;

            const unevenSplitConfiguration = actualEditSplitConfiguration.merge({
                shares: actualEditSplitConfiguration.shares.map(bps =>
                    bps.billPayerId === billPayerId ? bps.merge({share}) : bps,
                ),
            });

            const hasEditedIds = BillPayerHelpers.getLockedIds(
                state.hasEditedIds,
                billPayerId,
                unevenSplitConfiguration,
            );

            const {validationErrors, splitConfiguration: editSplitConfiguration} =
                BillPayerHelpers.updateAndValidateConfiguration(unevenSplitConfiguration, hasEditedIds);

            const editingShares = state.editingShares
                .set(billPayerId, newShare)
                .filter((_, key) => hasEditedIds.includes(key));

            return state.merge({
                validationErrors,
                hasEditedIds,
                editingShares,
                editSplitConfiguration,
            });
        },
        [Actions.clearBillPayerEdit.type]: (state, {payload}) =>
            state.merge({
                editingShares: state.editingShares.delete(payload.billPayerId),
            }),
        [Actions.shareTypeChanged.type]: (state, {payload}) => {
            const {billPayerId, newType, actualEditSplitConfiguration} = payload;

            const unevenSplitConfiguration = actualEditSplitConfiguration.merge({
                shares: actualEditSplitConfiguration.shares.map(bps =>
                    bps.billPayerId === billPayerId
                        ? bps.merge({
                              share:
                                  newType === BillPayerShare.CONSTANTS.TYPES.PERCENTAGE
                                      ? bps.share / 100
                                      : bps.share * 100,
                              type: newType,
                          })
                        : bps,
                ),
            });

            const hasEditedIds = BillPayerHelpers.getLockedIds(
                state.hasEditedIds,
                billPayerId,
                unevenSplitConfiguration,
            );

            const {validationErrors, splitConfiguration: editSplitConfiguration} =
                BillPayerHelpers.updateAndValidateConfiguration(unevenSplitConfiguration, hasEditedIds);

            return state.merge({
                validationErrors,
                hasEditedIds,
                editSplitConfiguration,
            });
        },
        [Actions.saveSplit.type]: state => state.merge({saving: true}),
        [Actions.saveSplitSuccess.type]: () => new BillPayersState(),
        [Actions.saveSplitFailed.type]: state => state.merge({saving: false}),
        [Actions.removeBillPayerFromSplit.type]: (state, {payload}) => state.merge({removingId: payload.billPayerId}),
        [Actions.removeBillPayerFromSplitSuccess.type]: (state, {payload}) => {
            const {validationErrors, splitConfiguration: editSplitConfiguration} =
                BillPayerHelpers.updateAndValidateConfiguration(payload.newConfiguration, state.hasEditedIds);

            return state.merge({
                removingId: null,
                validationErrors,
                editSplitConfiguration,
            });
        },
        [Actions.removeBillPayerFromSplitFailed.type]: state => state.merge({removingId: null}),
        [Actions.removeBillPayerFromSplitAborted.type]: state => state.merge({removingId: null}),
    },
    new BillPayersState(),
);
